import React from 'react';
import { AbacProvider } from 'react-abac';

import { UserRole } from 'core/constants';
import SessionUser, { hasAllProductLicenses, hasFunctionalModuleLicense } from 'stores/Session/SessionUser';
import { useStore } from 'utils/hooks/store';
import { UuidableName } from 'utils/api/types';
import { UserLicensesResponse } from 'utils/api/modules/user/user.types';

import { SideBarPermissions, UserRoleRules } from './types';
import SideBar from '../SideBar';

const MoreAppsAllowedPermission = (licenses: UserLicensesResponse): boolean => !hasAllProductLicenses(licenses);

const UserHasNoLicenses = (licenses: UserLicensesResponse): boolean =>
  Object.values(licenses).every((license) => !license);

const LoneWorkerOnlyBlockedPermission = (licenses: UserLicensesResponse): boolean => {
  if (UserHasNoLicenses(licenses)) {
    return false;
  }

  const {
    protectorAlarms,
    // our logic is not relying on these licenses
    protectorMonitoringSelf,
    protectorMonitoringVatix,
    protectorNotify,
    ...otherLicenses
  } = licenses;
  if (protectorAlarms && Object.values(otherLicenses).every((license) => license === false)) {
    return false;
  }
  return true;
};

const LoneWorkerAllowedPermission = (licenses: UserLicensesResponse): boolean => licenses.protectorAlarms;

const WorkflowsAllowedPermission = (licenses: UserLicensesResponse): boolean => licenses.workflows;

const WorkflowsAllowedPermissionForNotifications = (licenses: UserLicensesResponse): boolean => licenses.workflows;

const UserOrganisationActivityMapAllowedPermission = (user?: SessionUser): boolean =>
  Boolean(user?.organisation.activityMap);

const getCleanedDashboards = (user?: SessionUser): Record<string, UuidableName[]> =>
  Object.fromEntries(
    Object.entries(user?.dashboards?.analytics || {}).filter(([, value]) => Array.isArray(value) && value.length > 0)
  );

const hasAnyDashboard = (licenses?: UserLicensesResponse, user?: SessionUser): boolean => {
  if (licenses && UserHasNoLicenses(licenses)) {
    return false;
  }
  const cleanedDashboards = getCleanedDashboards(user);
  return Boolean(user?.dashboards?.analytics) && Object.keys(cleanedDashboards).length > 0;
};

const rules: UserRoleRules = {
  [UserRole.Admin]: {
    [SideBarPermissions.HOME_PAGE_VISIBLE]: true,
    [SideBarPermissions.INBOX_VISIBLE]: hasFunctionalModuleLicense,
    [SideBarPermissions.ACTIONS_VISIBLE]: LoneWorkerOnlyBlockedPermission,
    [SideBarPermissions.LONE_WORKING_VISIBLE]: LoneWorkerAllowedPermission,
    [SideBarPermissions.LONE_WORKING_MANAGE_VISIBLE]: true,
    [SideBarPermissions.LONE_WORKING_DEVICES_VISIBLE]: true,
    [SideBarPermissions.LONE_WORKING_USAGE_VISIBLE]: true,
    [SideBarPermissions.LONE_WORKING_ARPS_VISIBLE]: true,
    [SideBarPermissions.LONE_WORKING_MAP_VISIBLE]: UserOrganisationActivityMapAllowedPermission,
    [SideBarPermissions.AUDITS_VISIBLE]: WorkflowsAllowedPermission,
    [SideBarPermissions.ANALYTICS_VISIBLE]: (data?: { licenses: UserLicensesResponse; user?: SessionUser }) =>
      hasAnyDashboard(data?.licenses, data?.user),
    [SideBarPermissions.ADMIN_VISIBLE]: true,
    [SideBarPermissions.PROFILE_SETTINGS_NOTIFICATIONS_VISIBLE]: WorkflowsAllowedPermissionForNotifications,
    [SideBarPermissions.MORE_APPS_VISIBLE]: MoreAppsAllowedPermission,
  },
  [UserRole.AccountManager]: {
    [SideBarPermissions.HOME_PAGE_VISIBLE]: true,
    [SideBarPermissions.INBOX_VISIBLE]: hasFunctionalModuleLicense,
    [SideBarPermissions.ACTIONS_VISIBLE]: LoneWorkerOnlyBlockedPermission,
    [SideBarPermissions.LONE_WORKING_VISIBLE]: LoneWorkerAllowedPermission,
    [SideBarPermissions.LONE_WORKING_MANAGE_VISIBLE]: true,
    [SideBarPermissions.LONE_WORKING_DEVICES_VISIBLE]: true,
    [SideBarPermissions.LONE_WORKING_USAGE_VISIBLE]: true,
    [SideBarPermissions.LONE_WORKING_ARPS_VISIBLE]: true,
    [SideBarPermissions.LONE_WORKING_MAP_VISIBLE]: UserOrganisationActivityMapAllowedPermission,
    [SideBarPermissions.AUDITS_VISIBLE]: WorkflowsAllowedPermission,
    [SideBarPermissions.ANALYTICS_VISIBLE]: (data?: { licenses: UserLicensesResponse; user?: SessionUser }) =>
      hasAnyDashboard(data?.licenses, data?.user),
    [SideBarPermissions.ADMIN_VISIBLE]: true,
    [SideBarPermissions.PROFILE_SETTINGS_NOTIFICATIONS_VISIBLE]: WorkflowsAllowedPermissionForNotifications,
    [SideBarPermissions.MORE_APPS_VISIBLE]: MoreAppsAllowedPermission,
  },
  [UserRole.Manager]: {
    [SideBarPermissions.HOME_PAGE_VISIBLE]: true,
    [SideBarPermissions.INBOX_VISIBLE]: hasFunctionalModuleLicense,
    [SideBarPermissions.ACTIONS_VISIBLE]: LoneWorkerOnlyBlockedPermission,
    [SideBarPermissions.LONE_WORKING_VISIBLE]: LoneWorkerAllowedPermission,
    [SideBarPermissions.LONE_WORKING_MANAGE_VISIBLE]: true,
    [SideBarPermissions.LONE_WORKING_DEVICES_VISIBLE]: true,
    [SideBarPermissions.LONE_WORKING_USAGE_VISIBLE]: true,
    [SideBarPermissions.LONE_WORKING_ARPS_VISIBLE]: true,
    [SideBarPermissions.LONE_WORKING_MAP_VISIBLE]: UserOrganisationActivityMapAllowedPermission,
    [SideBarPermissions.AUDITS_VISIBLE]: WorkflowsAllowedPermission,
    [SideBarPermissions.ANALYTICS_VISIBLE]: (data?: { licenses: UserLicensesResponse; user?: SessionUser }) =>
      hasAnyDashboard(data?.licenses, data?.user),
    [SideBarPermissions.ADMIN_VISIBLE]: false,
    [SideBarPermissions.PROFILE_SETTINGS_NOTIFICATIONS_VISIBLE]: WorkflowsAllowedPermissionForNotifications,
    [SideBarPermissions.MORE_APPS_VISIBLE]: MoreAppsAllowedPermission,
  },
  [UserRole.ArcOperator]: {
    [SideBarPermissions.HOME_PAGE_VISIBLE]: false,
    [SideBarPermissions.INBOX_VISIBLE]: hasFunctionalModuleLicense,
    [SideBarPermissions.ACTIONS_VISIBLE]: LoneWorkerOnlyBlockedPermission,
    [SideBarPermissions.LONE_WORKING_VISIBLE]: LoneWorkerAllowedPermission,
    [SideBarPermissions.LONE_WORKING_MANAGE_VISIBLE]: false,
    [SideBarPermissions.LONE_WORKING_DEVICES_VISIBLE]: false,
    [SideBarPermissions.LONE_WORKING_USAGE_VISIBLE]: false,
    [SideBarPermissions.LONE_WORKING_ARPS_VISIBLE]: false,
    [SideBarPermissions.LONE_WORKING_MAP_VISIBLE]: UserOrganisationActivityMapAllowedPermission,
    [SideBarPermissions.AUDITS_VISIBLE]: WorkflowsAllowedPermission,
    [SideBarPermissions.ANALYTICS_VISIBLE]: false,
    [SideBarPermissions.ADMIN_VISIBLE]: false,
    [SideBarPermissions.PROFILE_SETTINGS_NOTIFICATIONS_VISIBLE]: WorkflowsAllowedPermissionForNotifications,
    [SideBarPermissions.MORE_APPS_VISIBLE]: false,
  },
  [UserRole.User]: {
    [SideBarPermissions.HOME_PAGE_VISIBLE]: true,
    [SideBarPermissions.INBOX_VISIBLE]: hasFunctionalModuleLicense,
    [SideBarPermissions.ACTIONS_VISIBLE]: LoneWorkerOnlyBlockedPermission,
    [SideBarPermissions.LONE_WORKING_VISIBLE]: false,
    [SideBarPermissions.LONE_WORKING_MANAGE_VISIBLE]: false,
    [SideBarPermissions.LONE_WORKING_DEVICES_VISIBLE]: false,
    [SideBarPermissions.LONE_WORKING_USAGE_VISIBLE]: false,
    [SideBarPermissions.LONE_WORKING_ARPS_VISIBLE]: false,
    [SideBarPermissions.LONE_WORKING_MAP_VISIBLE]: UserOrganisationActivityMapAllowedPermission,
    [SideBarPermissions.AUDITS_VISIBLE]: WorkflowsAllowedPermission,
    [SideBarPermissions.ANALYTICS_VISIBLE]: false,
    [SideBarPermissions.ADMIN_VISIBLE]: false,
    [SideBarPermissions.PROFILE_SETTINGS_NOTIFICATIONS_VISIBLE]: WorkflowsAllowedPermissionForNotifications,
    [SideBarPermissions.MORE_APPS_VISIBLE]: MoreAppsAllowedPermission,
  },
};

const SideBarPermissionsWrapper: React.FC = () => {
  const {
    session: { user },
  } = useStore();
  return (
    <AbacProvider roles={user ? [user.role] : []} permissions={[]} rules={rules}>
      <SideBar />
    </AbacProvider>
  );
};

export default SideBarPermissionsWrapper;
